// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, forwardRef, Ref } from "react";
import { css } from "styled-components";

interface OwnProps {
    cancelButtonText: string;
    submitButtonText: string;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmit: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    submitTestSelector?: string;
    cancelTestSelector?: string;
    submitDisabled?: boolean;
    cancelDisabled?: boolean;
    submitButtonProps?: ButtonProps;
    cancelButtonProps?: ButtonProps;
    isSticky?: boolean;
}

type Props = OwnProps;

export interface TwoButtonActionModalStyles {
    container?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const twoButtonActionModalStyles: TwoButtonActionModalStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-top: 1px solid ${color("common.border")};
            padding-top: 22px;
            margin-top: 22px;
            width: 100%;
            padding-bottom: 5px;
            background: ${color("common.white")};
            padding-bottom: 5px;
            ${({ isSticky }: any) =>
                isSticky &&
                `
                position: absolute;
                padding: 22px;
                bottom: 0;
                left: 0;
                width: 100%;
                `}
        `,
    },
    cancelButton: {
        color: "secondary",
        variant: "secondary",
        sizeVariant: "small",
    },
    submitButton: {
        color: "primary",
        variant: "primary",
        sizeVariant: "small",
    },
};

const TwoButtonActionModal: FC<Props> = (
    {
        onCancel,
        onSubmit,
        cancelButtonText,
        submitButtonText,
        cancelDisabled,
        submitDisabled,
        cancelTestSelector,
        submitTestSelector,
        submitButtonProps,
        cancelButtonProps,
        isSticky = false,
    },
    ref: Ref<HTMLDivElement>,
) => {
    const formCancelHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        onCancel(e);
    };

    const formSubmitHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        onSubmit(e);
    };

    const styles = twoButtonActionModalStyles;

    return (
        <StyledWrapper {...{ isSticky }} {...styles.container} ref={ref}>
            <Button
                {...styles.cancelButton}
                onClick={formCancelHandler}
                data-test-selector={cancelTestSelector}
                disabled={submitDisabled}
                {...cancelButtonProps}
            >
                {cancelButtonText}
            </Button>
            <Button
                {...styles.submitButton}
                onClick={formSubmitHandler}
                data-test-selector={submitTestSelector}
                disabled={cancelDisabled}
                {...submitButtonProps}
            >
                {submitButtonText}
            </Button>
        </StyledWrapper>
    );
};

export default forwardRef(TwoButtonActionModal);
